
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useTrackingSocket } from "./TrackingSocketContext";
import { useNotification, NotificationSeverity, NotificationPositions } from "./NotificationContext";

const TrackingProvidersContext = createContext(null);

export const useTrackingProviders = () => {
  const context = useContext(TrackingProvidersContext);
  if (!context) {
    throw new Error('useTrackingProviders debe ser usado dentro de TrackingProvidersProvider');
  }
  return context;
};

const AVAILABLE_PROVIDERS = [
  { id: 'all', name: 'Todos' },
  { id: 'securita', name: 'Securita' },
  { id: 'kontigo', name: 'Kontigo' },
  { id: 'tecnosur', name: 'Tecnosur' },
  { id: 'coltrack', name: 'Coltrack' },
  { id: 'protrack365', name: 'Protrack365' },
  { id: 'satelitales', name: 'Satelitales' },
  { id: 'rastreoflotas', name: 'Rastreoflotas' },
  { id: 'tracker', name: 'Tracker' },
  { id: 'satrack', name: 'Satrack' }
];

export const TrackingProvidersProvider = ({ children }) => {
  const [vehiclePositions, setVehiclePositions] = useState(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [trackedEvents, setTrackedEvents] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isInitialized, setIsInitialized] = useState(false);
  
  const [activeProvider, setActiveProvider] = useState(() => {
    const saved = localStorage.getItem('selectedTrackingProvider');
    return saved ? JSON.parse(saved) : AVAILABLE_PROVIDERS[0];
  });
  
  const subscriptionsRef = useRef(new Set());
  const initializationTimeout = useRef(null);
  const isFirstLoad = useRef(true);
  
  const { socket } = useTrackingSocket();
  const { showNotification } = useNotification();

  // Normaliza posiciones del servidor (plate,latitude,...) al formato que esperan los componentes (UnitPlate,Latitude,...)
  const normalizePosition = useCallback((pos) => {
    if (!pos) return pos;
    // Si ya tiene UnitPlate, probablemente ya está en formato frontend
    if (pos.UnitPlate && pos.Latitude !== undefined) return pos;
    return {
      ...pos,
      // Identificación
      UnitPlate: pos.UnitPlate || pos.plate || pos.Placa || '',
      UnitId: pos.UnitId || pos.plate || pos.Placa || '',
      Placa: pos.Placa || pos.plate || pos.UnitPlate || '',
      // Coordenadas
      Latitude: pos.Latitude ?? pos.latitude ?? pos.Latitud ?? null,
      Longitude: pos.Longitude ?? pos.longitude ?? pos.Longitud ?? null,
      // Movimiento
      GpsSpeed: pos.GpsSpeed ?? pos.speed ?? 0,
      Direction: pos.Direction ?? pos.orientation ?? 0,
      // Estado
      Ignition: pos.Ignition ?? (Number(pos.speed || pos.GpsSpeed || 0) > 0 ? 1 : 0),
      // Ubicación
      Domicilio: pos.Domicilio || pos.address || pos.Direccion || '',
      Direccion: pos.Direccion || pos.address || pos.Domicilio || '',
      // Tiempo
      ReportDate: pos.ReportDate || pos.gpsDate || pos.updatedAt || '',
      // Proveedor
      Provider: pos.Provider || pos.provider || pos.providerName || 'unknown',
      provider: pos.provider || pos.Provider || pos.providerName || 'unknown',
    };
  }, []);

  const generateVehicleKey = useCallback((vehicle, providerName = null) => {
    const provider = providerName || vehicle.provider || vehicle.Provider || 'unknown';
    const id = vehicle.plate || vehicle.UnitPlate || vehicle.UnitId || vehicle.GpsIdentif || vehicle.Placa;
    
    if (!id) return null;
    return `${provider}_${id}`;
  }, []);

  const requestInitialData = useCallback((providerId = null) => {
    if (!socket || !socket.connected) {
      return;
    }

    const targetProvider = providerId || activeProvider.id;
    
    socket.emit('tracking:request_initial_state', {
      provider: targetProvider !== 'all' ? targetProvider : null,
      timestamp: Date.now(),
      clientId: socket.id,
      forceRefresh: isFirstLoad.current
    });

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }, [socket, activeProvider.id]);

  useEffect(() => {
    if (!socket) {
      setConnectionStatus('disconnected');
      setIsConnected(false);
      setIsInitialized(false);
      return;
    }

    // Verificar el estado de conexión actual del socket
    const isSocketConnected = socket.connected;
    setConnectionStatus(isSocketConnected ? 'connecting' : 'disconnected');

    if (initializationTimeout.current) {
      clearTimeout(initializationTimeout.current);
    }

    const handleAllPositions = (data) => {
      try {
        // El servidor envía { positions: [...], configuredPlates: [...], ... }
        const positionsList = Array.isArray(data) ? data : (data?.positions || []);
        const positionsMap = new Map();
        const duplicatesFound = new Set();
        
        positionsList.forEach(position => {
          const normalized = normalizePosition(position);
          const vehicleKey = generateVehicleKey(normalized);
          if (vehicleKey) {
            if (positionsMap.has(vehicleKey)) {
              duplicatesFound.add(vehicleKey);
              const existing = positionsMap.get(vehicleKey);
              const existingDate = new Date(existing.ReportDate || existing.lastUpdate || 0);
              const currentDate = new Date(normalized.ReportDate || 0);
              
              if (currentDate >= existingDate) {
                positionsMap.set(vehicleKey, {
                  ...normalized,
                  lastUpdate: new Date().toISOString(),
                  provider: normalized.provider
                });
              }
            } else {
              positionsMap.set(vehicleKey, {
                ...normalized,
                lastUpdate: new Date().toISOString(),
                provider: normalized.provider
              });
            }
          }
        });
        
        setVehiclePositions(positionsMap);
        setLastUpdate(new Date().toISOString());
        setIsConnected(true);
        setConnectionStatus('connected');
        setIsInitialized(true);
        
      } catch (error) {
        console.error("Error procesando posiciones:", error);
        showNotification({
          message: `Error procesando posiciones: ${error.message}`,
          severity: NotificationSeverity.ERROR,
          position: NotificationPositions.TOP_RIGHT,
          autoHideDuration: 5000,
        });
      }
    };

    const handleBatchUpdate = (data) => {
      try {
        const vehicles = data?.vehicles || [];
        if (vehicles.length === 0) return;

        setVehiclePositions(prevPositions => {
          const newPositions = new Map(prevPositions);
          for (const position of vehicles) {
            const normalized = normalizePosition(position);
            const vehicleKey = generateVehicleKey(normalized, data.provider);
            if (vehicleKey) {
              newPositions.set(vehicleKey, {
                ...prevPositions.get(vehicleKey),
                ...normalized,
                lastUpdate: new Date().toISOString(),
                provider: normalized.provider || data.provider || 'unknown'
              });
            }
          }
          return newPositions;
        });

        setLastUpdate(new Date().toISOString());
        setIsConnected(true);
        setConnectionStatus('connected');
        setIsInitialized(true);
      } catch (error) {
        console.error('Error procesando batch update:', error);
      }
    };

    const handleProviderPositions = (providerName) => (data) => {
      try {
        const positions = Array.isArray(data) ? data : (data?.positions || data?.vehicles || []);
        setVehiclePositions(prevPositions => {
          const newPositions = new Map(prevPositions);
          
          positions.forEach(position => {
            const normalized = normalizePosition(position);
            const vehicleKey = generateVehicleKey(normalized, providerName);
            if (vehicleKey) {
              newPositions.set(vehicleKey, {
                ...normalized,
                lastUpdate: new Date().toISOString(),
                provider: providerName
              });
            }
          });
          
          return newPositions;
        });
        
        setLastUpdate(new Date().toISOString());
        setIsConnected(true);
        setConnectionStatus('connected');
        setIsInitialized(true);
        
      } catch (error) {
        showNotification({
          message: `Error procesando datos de ${providerName}: ${error.message}`,
          severity: NotificationSeverity.ERROR,
          position: NotificationPositions.TOP_RIGHT,
          autoHideDuration: 5000,
        });
      }
    };

    const handleVehicleEvent = (eventData) => {
      try {
        const newEvent = {
          ...eventData,
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          receivedAt: new Date().toISOString()
        };
        
        setTrackedEvents(prevEvents => {
          const updatedEvents = [newEvent, ...prevEvents].slice(0, 100);
          return updatedEvents;
        });
        
        if (eventData.isConfiguredVehicle) {
          showNotification({
            message: `${eventData.details?.eventTypeName || eventData.type}: ${eventData.vehicleId}`,
            severity: NotificationSeverity.WARNING,
            position: NotificationPositions.TOP_RIGHT,
            autoHideDuration: 8000
          });
        }
      } catch (error) {
        // Error procesando evento vehicular
      }
    };

    const handleTrackingStatus = (status) => {
      setConnectionStatus(status.connected ? 'connected' : 'disconnected');
      setIsConnected(status.connected);
      
      if (status.connected && !isInitialized && socket?.connected) {
        setTimeout(() => requestInitialData(), 500);
      }
    };

    const handleInitializationComplete = (data) => {
      setIsInitialized(true);
      
      showNotification({
        message: "Sistema de tracking inicializado correctamente",
        severity: NotificationSeverity.SUCCESS,
        position: NotificationPositions.TOP_RIGHT,
        autoHideDuration: 3000,
      });
    };

    const eventListeners = [
      ['connect', () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        
        // Solicitar datos iniciales después de conectar
        if (!isInitialized) {
          setTimeout(() => requestInitialData(), 1000);
        }
      }],
      ['disconnect', () => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
      }],
      ['tracking:all_positions', handleAllPositions],
      ['tracking:initial_state', handleAllPositions],
      ['tracking:batch_update', handleBatchUpdate],
      ['tracking:system_status', handleTrackingStatus],
      ['tracking:initialization_complete', handleInitializationComplete],
      ['vehicle:event', handleVehicleEvent],
      ['tracking:position_update', (data) => {
        const normalized = normalizePosition(data);
        const vehicleKey = generateVehicleKey(normalized);
        if (vehicleKey) {
          setVehiclePositions(prev => {
            const newMap = new Map(prev);
            newMap.set(vehicleKey, {
              ...prev.get(vehicleKey),
              ...normalized,
              lastUpdate: new Date().toISOString()
            });
            return newMap;
          });
        }
      }],
      ['tracking:securita:positions', handleProviderPositions('securita')],
      ['tracking:kontigo:positions', handleProviderPositions('kontigo')],
      ['tracking:tecnosur:positions', handleProviderPositions('tecnosur')],
      ['tracking:coltrack:positions', handleProviderPositions('coltrack')],
      ['tracking:protrack365:positions', handleProviderPositions('protrack365')],
      ['tracking:satelitales:positions', handleProviderPositions('satelitales')],
      ['tracking:cellvi:positions', handleProviderPositions('cellvi')]
    ];

    const currentSubscriptions = subscriptionsRef.current;

    eventListeners.forEach(([event, handler]) => {
      socket.on(event, handler);
      currentSubscriptions.add(event);
    });

    const initializeData = () => {
      // Solo intentar solicitar datos si el socket está conectado
      if (!socket.connected) {
        
        // Esperar a que el socket se conecte
        const checkConnection = setInterval(() => {
          if (socket.connected) {
            clearInterval(checkConnection);
            requestInitialData();
          }
        }, 500);
        
        // Timeout de 10 segundos
        setTimeout(() => {
          clearInterval(checkConnection);
          if (!socket.connected) {
            showNotification({
              message: "No se pudo conectar al sistema de tracking",
              severity: NotificationSeverity.WARNING,
              position: NotificationPositions.TOP_RIGHT,
              autoHideDuration: 5000,
            });
          }
        }, 10000);
        
        return;
      }
      
      // Socket ya está conectado, solicitar datos inmediatamente
      requestInitialData();
      
      initializationTimeout.current = setTimeout(() => {
        if (!isInitialized && socket.connected) {
          requestInitialData();
        }
      }, 5000);
    };

    initializeData();

    return () => {
      eventListeners.forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      currentSubscriptions.clear();
      
      if (initializationTimeout.current) {
        clearTimeout(initializationTimeout.current);
      }
    };
  }, [socket, showNotification, requestInitialData, isInitialized, generateVehicleKey, normalizePosition]);

  useEffect(() => {
    if (socket && socket.connected && activeProvider) {
      setIsInitialized(false);
      
      setTimeout(() => {
        requestInitialData(activeProvider.id);
      }, 100);
    }
  }, [activeProvider, socket, requestInitialData]);

  const changeProvider = useCallback((providerId) => {
    if (providerId !== activeProvider.id) {
      const known = AVAILABLE_PROVIDERS.find(p => p.id === providerId);
      const newProvider = known || { id: providerId, name: providerId.charAt(0).toUpperCase() + providerId.slice(1) };
      setActiveProvider(newProvider);
      localStorage.setItem('selectedTrackingProvider', JSON.stringify(newProvider));
      setIsInitialized(false);
      
      showNotification({
        message: `Proveedor cambiado a: ${newProvider.name}`,
        severity: NotificationSeverity.INFO,
        position: NotificationPositions.TOP_RIGHT,
        autoHideDuration: 2000,
      });
    }
  }, [activeProvider.id, showNotification]);

  const selectVehicle = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedVehicle(null);
  }, []);

  const forceUpdate = useCallback(async () => {
    if (!socket) {
      showNotification({
        message: 'No hay conexión con el servidor de tracking',
        severity: NotificationSeverity.ERROR,
        position: NotificationPositions.TOP_RIGHT,
        autoHideDuration: 4000,
      });
      return;
    }

    try {
      setIsInitialized(false);
      requestInitialData();
      
      showNotification({
        message: 'Actualizando posiciones vehiculares...',
        severity: NotificationSeverity.INFO,
        position: NotificationPositions.TOP_RIGHT,
        autoHideDuration: 3000,
      });
    } catch (error) {
      showNotification({
        message: `Error al actualizar posiciones: ${error.message}`,
        severity: NotificationSeverity.ERROR,
        position: NotificationPositions.TOP_RIGHT,
        autoHideDuration: 5000,
      });
    }
  }, [socket, showNotification, requestInitialData]);

  const getVehiclesByProvider = useCallback((providerId) => {
    if (providerId === 'all') {
      return Array.from(vehiclePositions.values());
    }
    
    return Array.from(vehiclePositions.values()).filter(
      vehicle => vehicle.provider === providerId
    );
  }, [vehiclePositions]);

  const findVehicle = useCallback((vehicleId, providerId = null) => {
    if (providerId) {
      const key = `${providerId}_${vehicleId}`;
      return vehiclePositions.get(key) || null;
    }
    
    for (const [key, vehicle] of vehiclePositions) {
      if (key.endsWith(`_${vehicleId}`)) {
        return vehicle;
      }
    }
    
    return null;
  }, [vehiclePositions]);

  const getTrackingStats = useCallback(() => {
    const providerStats = {};
    let totalVehicles = 0;
    let activeVehicles = 0;
    
    for (const vehicle of vehiclePositions.values()) {
      const provider = vehicle.provider || 'unknown';
      
      if (!providerStats[provider]) {
        providerStats[provider] = { total: 0, active: 0 };
      }
      
      providerStats[provider].total++;
      totalVehicles++;
      
      const isActive = (vehicle.GpsSpeed && parseFloat(vehicle.GpsSpeed) > 0) || 
                      vehicle.Ignition === 1;
      
      if (isActive) {
        providerStats[provider].active++;
        activeVehicles++;
      }
    }
    
    return {
      totalVehicles,
      activeVehicles,
      providerStats,
      lastUpdate,
      connectionStatus,
      eventsCount: trackedEvents.length
    };
  }, [vehiclePositions, lastUpdate, connectionStatus, trackedEvents.length]);

  useEffect(() => {
    if (isInitialized && isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }, [isInitialized]);

  const contextValue = {
    providers: AVAILABLE_PROVIDERS,
    activeProvider,
    changeProvider,
    vehicles: Array.from(vehiclePositions.values()),
    vehiclePositions,
    isConnected,
    connectionStatus,
    lastUpdate,
    selectedVehicle,
    trackedEvents,
    isInitialized,
    selectVehicle,
    clearSelection,
    forceUpdate,
    requestInitialData,
    getVehiclesByProvider,
    findVehicle,
    getTrackingStats,
    totalVehicles: vehiclePositions.size,
    isLoading: connectionStatus === 'connecting' || !isInitialized,
    isError: connectionStatus === 'error',
    isProviderActive: (providerId) => activeProvider?.id === providerId,
    getProviderById: (providerId) => AVAILABLE_PROVIDERS.find(p => p.id === providerId)
  };

  return (
    <TrackingProvidersContext.Provider value={contextValue}>
      {children}
    </TrackingProvidersContext.Provider>
  );
};

TrackingProvidersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};